<template>
	<section class="code-edit">
		<h1 v-if="data" v-translate class="is-size-1">
			Edit registration code
		</h1>
		<form ref="form" @submit.prevent="save">
			<div v-if="data">
				<b-field grouped>
					<b-field :label="$gettext('Code')" expanded>
						<b-input v-model="data.code" name="code" required></b-input>
					</b-field>
				</b-field>
				<b-field grouped>
					<b-field :label="$gettext('Description')" expanded>
						<b-input v-model="data.description" name="description"></b-input>
					</b-field>
				</b-field>
				<b-field grouped>
					<b-field :label="$gettext('Role')" expanded>
						<b-select v-model="selectedRole" expanded required>
							<option v-for="role in allRoles" :key="role._id" :value="role._id">
								{{ role.name }}
							</option>
						</b-select>
					</b-field>
				</b-field>
				<b-field grouped>
					<b-field :label="$gettext('Platform flavor')" expanded>
						<b-select v-model="data.platformFlavor" expanded required>
							<option v-for="flavor in platformFlavors" :key="flavor._id" :value="flavor._id">
								{{ flavor.name }}
							</option>
						</b-select>
					</b-field>
				</b-field>
				<b-field grouped>
					<b-field :label="$gettext('Country')" expanded>
						<b-select v-model="data.country" expanded>
							<option v-for="country in countries" :key="country.code" :value="country.code">
								{{ country.name }}
							</option>
						</b-select>
					</b-field>
				</b-field>
				<b-field grouped>
					<b-field :label="$gettext('Language')" expanded>
						<b-select v-model="data.language" expanded>
							<option v-for="language in availableLanguages" :key="language.code" :value="language.code">
								{{ language.name }}
							</option>
						</b-select>
					</b-field>
				</b-field>
				<b-field :label="$gettext('Code disabled')">
					<b-switch v-model='data.disabled' name='disabled' type='is-primary'>
						{{ data.disabled ? $gettext('Yes') : $gettext('No') }}
					</b-switch>
				</b-field>
        <b-field :label="$gettext('Is registration context code')">
          <b-switch v-model='data.isContextRegistrationCode' name='context' type='is-primary'>
            {{ data.isContextRegistrationCode ? $gettext('Yes') : $gettext('No') }}
          </b-switch>
        </b-field>
				<div v-if="access.scopes.includes('application:read')">
					<h2 v-translate class="is-size-2 mt-6">Applications</h2>
					<associate
						:name="$gettext('applications')"
						:associated="associated.applications"
						:unassociated="unassociated.applications"
						key-name="_id"
						:display-keys="['name']"
						:search-keys="['name']"
						@change="handleChangeApplications"
					></associate>

					<h2 v-translate class="is-size-2 mt-6">Hide applications</h2>
					<associate
						:name="$gettext('applications')"
						:associated="associated.hiddenApplications"
						:unassociated="unassociated.hiddenApplications"
						key-name="_id"
						:display-keys="['name']"
						:search-keys="['name']"
						@change="handleChangeHiddenApplications"
					></associate>

					<div v-if="associated.applications.length > 0">
						<h2 v-translate class="is-size-2 mt-6">Limit scopes</h2>
						<p v-translate class="is-size-7">
							Add scopes here if you want to limit this code's access in some applications.
						</p>
						<associate
							:name="$gettext('forbidden scopes')"
							:associated="associated.removedScopes"
							:unassociated="unassociated.removedScopes"
							associated-box-type="is-warning"
							@change="handleChangeScopes"
						></associate>
					</div>
				</div>
				<div v-if="access.scopes.includes('organization:read')">
					<h2 v-translate class="is-size-2 mt-6">Organizations</h2>
					<associate
						:name="$gettext('organizations')"
						:associated="associated.organizations"
						:unassociated="unassociated.organizations"
						associated-box-type="is-primary"
						key-name="_id"
						:display-keys="['name']"
						:search-keys="['name']"
						@change="handleChangeOrganizations"
					></associate>
				</div>
				<div v-if="access.scopes.includes('study:read')">
					<h2 v-translate class="is-size-2 mt-6">Studies</h2>
					<associate
						:name="$gettext('studies')"
						:associated="associated.studies"
						:unassociated="unassociated.studies"
						key-name="_id"
						:display-keys="['name']"
						:search-keys="['name']"
						associated-box-type="is-warning"
						@change="handleChangeStudy"
					></associate>
				</div>
				<b-loading v-model="isLoading" :is-full-page="false"></b-loading>
			</div>
			<div class="buttons is-pulled-right mt-6">
				<b-button @click="back"><translate>Back to list</translate></b-button>
				<b-button
					v-if="data && access.scopes.includes('code:write')"
					type="is-primary"
					icon-left="content-save"
					@click="save"
				>
					<translate>Save</translate>
				</b-button>
			</div>
		</form>
	</section>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { sortBy, compose, toLower, propOr, pick, difference, uniq } from 'ramda';
import { errorToast, successToast } from '@moveup/app-core/mixins';
import { Associate } from '@moveup/app-core/components';
import { getCountries } from '@moveup/app-core/utils';
import { fetchAll } from '@/utils/fetchAll';

const concat = (p, c) => p.concat(c);

export default {
	name: 'EditCode',
	components: { Associate },
	mixins: [errorToast, successToast],
	data() {
		return {
			id: undefined,
			isLoading: false,
			data: undefined,
			allApplications: [],
			allOrganizations: [],
			allScopes: [],
			allRoles: [],
			platformFlavors: [],
			countries: [],
			associated: {
				applications: [],
				removedScopes: [],
				organizations: [],
				studies: [],
				hiddenApplications: [],
			},
			unassociated: {
				applications: [],
				removedScopes: [],
				organizations: [],
				studies: [],
				hiddenApplications: [],
			},
			selectedRole: undefined,
			availableLanguages: undefined,//[{code: 'nl'}, {code: 'fr'}, {code: 'en'}],
		};
	},
	computed: {
		...mapState('auth', ['access']),
		missingScopes() {
			return (
				this.associated.removedScopes.length > 0 &&
				difference(
					this.associated.applications.map(app => app.scopes).reduce(concat, []),
					this.associated.removedScopes,
				)
			);
		},
	},
	mounted() {
		if (this.$route.params.id) this.id = this.$route.params.id;
		this.loadAsyncData();
	},
	methods: {
		...mapActions(
			'app', ['getCode', 'getApplications', 'getOrganizations',
				'updateCode', 'getRoles', 'createCode', 'getPlatformFlavors', 'getAllStudies', 'getLanguages']
		),
		updateData({ newVal }) {
			this.data = newVal;
		},
		save() {
			this.$refs.form.reportValidity();
			if (this.$refs.form.checkValidity()) {
				this.data.applications = this.associated?.applications?.map(a => a.name) || [];
				this.data.hiddenApplications = this.associated?.hiddenApplications?.map(a => a.name) || [];
				this.data.removedScopes = this.associated?.removedScopes || [];
				this.data.organizations = this.associated?.organizations?.map(o => o._id) || [];
				this.data.studies = this.associated?.studies?.map(s => s._id) || [];
				this.data.role = this.selectedRole;

				const propertiesToPick = ['_id', 'code', 'description', 'disabled', 'platformFlavor','country', 'isContextRegistrationCode'];
				if (this.access.scopes.includes('organization:read'))
					propertiesToPick.push('organizations');
				if (this.access.scopes.includes('study:read'))
					propertiesToPick.push('studies');
				if (this.access.scopes.includes('application:read'))
					propertiesToPick.push('applications', 'removedScopes', 'hiddenApplications');
				if (this.access.scopes.includes('language:read'))
					propertiesToPick.push('language');
				//if (this.access.scopes.includes('role:read'))
					propertiesToPick.push('role');
				const patch = pick(propertiesToPick, this.data);

				if(this.id) {
					this.updateCode(patch)
						.then(result => {
							this.data = result;
							this.successToast(this.$gettext('Code updated'));
						})
						.catch(this.errorToast);
				}else{
					this.createCode(patch)
						.then(result => {
							this.data = result;
							this.successToast(this.$gettext('Code created'));
						})
						.catch(this.errorToast);
				}
			}
		},
		back() {
			this.$router.push('/codes');
		},
		async loadAsyncData() {
			this.isLoading = true;
			const params = {
				sortBy: 'name|asc',
				isService: false,
			};
			if(!this.id){
				this.data = {
					applications: [],
					hiddenApplications: [],
					organizations: [],
					removedScopes: []
				}
			}
			const promises = [];
			if (this.id && this.access.scopes.includes('code:read'))
				promises.push(
					this.getCode(this.id)
						.then(data => {
							this.data = data;
						})
						.catch(err => {
							this.data = undefined;
							this.errorToast(err);
						}),
				);
			if (this.access.scopes.includes('application:read'))
				promises.push(
					fetchAll(p => this.getApplications(p), params)
						.then(results => {
							this.allApplications = results;
						})
						.catch(err => {
							this.allApplications = [];
							this.errorToast(err);
						}),
				);
			if (this.access.scopes.includes('organization:read'))
				promises.push(
					fetchAll(p => this.getOrganizations(p), { sortBy: 'name|asc' })
						.then(results => {
							this.allOrganizations = results;
						})
						.catch(err => {
							this.allOrganizations = [];
							this.errorToast(err);
						}),
				);

			if (this.access.scopes.includes('study:read'))
				promises.push(
					fetchAll(p => this.getAllStudies(p))
						.then(results => {
							this.allStudies = results;
						})
						.catch(err => {
							this.allStudies = [];
							this.errorToast(err);
						}),
				);

			if (this.access.scopes.includes('role:read'))
				promises.push(
					fetchAll(p => this.getRoles(p), { sortBy: 'name|asc' })
						.then(results => {
							this.allRoles = results;
						})
						.catch(err => {
							this.allRoles = [];
							this.errorToast(err);
						}),
				);

			if (this.access.scopes.includes('code:read'))
				promises.push(
					fetchAll(p => this.getPlatformFlavors(p), { sortBy: 'name|asc' })
						.then(results => {
							this.platformFlavors = results;
						})
						.catch(err => {
							this.platformFlavors = [];
							this.errorToast(err);
						}),
				);
			// /languages (translation-service) is NOT paginated — sending page/perPage
			// returns empty, same as /scopes. Call bare and read the envelope.
			if (this.access.scopes.includes('language:read'))
				promises.push(
					this.getLanguages()
						.then(data => {
							this.availableLanguages = (data?.results || []).map(language => language);
						})
						.catch(err => {
							this.availableLanguages = undefined;
							this.errorToast(err);
						}),
				);

			await Promise.all(promises);

			if (this.access.scopes.includes('application:read')) {
				this.unassociated.applications = this.data.applications
					? this.allApplications.filter(item => !this.data?.applications?.includes(item.name))
					: this.allApplications;
				this.associated.applications = (this.data?.applications || [])
					.map(a => this.allApplications.find(aa => aa.name === a))
					.filter(Boolean);
				this.determineScopes();

				this.unassociated.hiddenApplications = this.data.hiddenApplications
					? this.allApplications.filter(item => !this.data?.hiddenApplications?.includes(item.name))
					: this.allApplications;
				this.associated.hiddenApplications = (this.data?.hiddenApplications || [])
					.map(a => this.allApplications.find(aa => aa.name === a))
					.filter(Boolean);
			}
			if (this.access.scopes.includes('organization:read')) {
				this.unassociated.organizations = this.data.organizations
					? this.allOrganizations.filter(item => !this.data?.organizations?.includes(item._id))
					: this.allOrganizations;
				this.associated.organizations = (this.data.organizations || [])
					.map(organizationId => this.allOrganizations.find(ao => ao._id === organizationId))
					.filter(Boolean);
			}
			if (this.access.scopes.includes('study:read')){
				this.unassociated.studies = this.data.studies
					? this.allStudies.filter(study => !this.data?.studies?.includes(study._id))
					: this.allStudies;
				this.associated.studies = (this.data?.studies || [])
					.map(s => this.allStudies.find(study => study._id === s))
					.filter(Boolean);
			}
			if (this.access.scopes.includes('role:read')) {
				this.selectedRole = this.data.role
			}

			this.countries = getCountries('en');
			this.isLoading = false;
		},
		handleChangeApplications({ associated, unassociated }) {
			this.associated.applications = associated;
			this.unassociated.applications = unassociated;
			this.determineScopes();
		},
		handleChangeHiddenApplications({ associated, unassociated }) {
			this.associated.hiddenApplications = associated;
			this.unassociated.hiddenApplications = unassociated;
		},
		handleChangeScopes({ associated, unassociated }) {
			this.associated.removedScopes = associated;
			this.unassociated.removedScopes = unassociated;
		},
		handleChangeOrganizations({ associated, unassociated }) {
			this.associated.organizations = associated;
			this.unassociated.organizations = unassociated;
		},
		handleChangeStudy({ associated, unassociated }) {
			this.associated.studies = associated;
			this.unassociated.studies = unassociated;
		},
		removeScopes() {
			this.associated.removedScopes = [];
		},
		determineScopes() {
			this.allScopes = uniq(
				this.associated.applications
					.filter(n => n)
					.map(app => app.scopes)
					.reduce(concat, []) || [],
			);
			this.unassociated.removedScopes = this.data.removedScopes
				? this.allScopes.filter(item => !this.data.removedScopes.includes(item))
				: this.allScopes;
			this.associated.removedScopes = this.data.removedScopes || [];
		},
	},
};
</script>
